import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { inboundPaymentsApi, type InboundPayment } from '../../api/inbound-payments.api.js';
import {
  paymentsApi,
  type PaymentListEntry,
  type PaymentStatus,
  type PaymentType,
} from '../../api/payments.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { ReviewDocumentsPanel } from '../../components/ReviewDocumentsPanel.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { PaymentScreeningPanel } from '../../components/PaymentScreeningPanel.js';

const STATUS_OPTIONS: PaymentStatus[] = [
  'PENDING',
  'PROCESSING',
  'SETTLED',
  'FAILED',
  'RETURNED',
  'RECALLED',
  'BLOCKED',
];
type PaymentTypeFilter = PaymentType | 'INCOMING';
const TYPE_OPTIONS: PaymentTypeFilter[] = ['INCOMING', 'SEPA_CT', 'SEPA_INSTANT', 'SWIFT'];
/** States from which an operator may still execute or reject. */
const OPEN_STATES: readonly string[] = ['PENDING', 'PROCESSING', 'BLOCKED'];

type Action = 'execute' | 'reject';

import { formatTransactionAmount as formatAmount } from '../../utils/transaction-money.js';
import { APP_LOCALE } from '../../utils/locale.js';

function formatTime(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(APP_LOCALE);
}

export function PaymentsPage({
  reviewId,
  onReviewClose,
}: { reviewId?: string; onReviewClose?: () => void } = {}): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canAct = can('payments.review');

  const [status, setStatus] = useState<PaymentStatus | ''>('');
  const [paymentType, setPaymentType] = useState<PaymentTypeFilter | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(reviewId ?? null);
  const [screeningReady, setScreeningReady] = useState(false);
  const [dialog, setDialog] = useState<{ action: Action } | null>(null);
  const [reason, setReason] = useState('');

  const filters = {
    ...(status !== '' ? { status } : {}),
    ...(paymentType !== '' && paymentType !== 'INCOMING' ? { paymentType } : {}),
  };

  const list = useQuery({
    queryKey: ['admin-payments', status, paymentType],
    queryFn: () => paymentsApi.listPayments(filters),
    enabled: !reviewId && paymentType !== 'INCOMING',
  });

  // Released incoming transfers are genuine payments too. They live in a
  // separate operational store because release/refusal is a different
  // workflow, but omitting them here made the payment history look empty after
  // money had visibly reached a customer's ledger.
  const includeReleasedIncoming =
    (paymentType === '' || paymentType === 'INCOMING') && (status === '' || status === 'SETTLED');
  const incoming = useQuery({
    queryKey: ['inbound-payments', 'APPROVED'],
    queryFn: () => inboundPaymentsApi.list('APPROVED'),
    enabled: !reviewId && includeReleasedIncoming,
  });

  const detail = useQuery({
    queryKey: ['admin-payment', selectedId],
    queryFn: () => paymentsApi.getPayment(selectedId as string),
    enabled: selectedId !== null,
  });

  const mutation = useMutation({
    mutationFn: ({ id, action, reason: r }: { id: string; action: Action; reason: string }) =>
      action === 'execute' ? paymentsApi.execute(id, r) : paymentsApi.reject(id, r),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      void qc.invalidateQueries({ queryKey: ['admin-payments'] });
      void qc.invalidateQueries({ queryKey: ['admin-payment', selectedId] });
      setDialog(null);
      setReason('');
    },
  });

  const selected = detail.data ?? null;

  const rows: Array<
    | { source: 'outgoing'; payment: PaymentListEntry }
    | { source: 'incoming'; payment: InboundPayment }
  > = [
    ...(list.data ?? []).map((payment) => ({ source: 'outgoing' as const, payment })),
    ...(incoming.data ?? []).map((payment) => ({ source: 'incoming' as const, payment })),
  ].sort(
    (a, b) => new Date(b.payment.createdAt).getTime() - new Date(a.payment.createdAt).getTime(),
  );

  const isLoading = list.isLoading || incoming.isLoading;
  const hasLoadError = list.isError || incoming.isError;

  const isOpenState = selected !== null && OPEN_STATES.includes(selected.status);

  return (
    <Box>
      {!reviewId && (
        <>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Payments
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Payment history and send queue. Released incoming transfers appear here; incoming
            decisions remain in Incoming Review.
          </Typography>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                size="small"
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value as PaymentStatus | '')}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All statuses</MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Type"
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value as PaymentTypeFilter | '')}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All types</MenuItem>
                {TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t === 'INCOMING' ? 'Incoming transfer' : t}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Paper>

          {hasLoadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Some payment records could not be loaded. The records shown below may be incomplete.
            </Alert>
          )}

          <Paper>
            {isLoading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Created</TableCell>
                    {/* Direction and rail were one column: it read INCOMING on one
                    row and SEPA_CT on the next, so which way the money went
                    could only be inferred by knowing which values meant
                    "incoming". They are two different questions and now two
                    columns — the money's direction against the customer's
                    account, and the rail that carried it. */}
                    <TableCell>Direction</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Counterparty</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    if (row.source === 'incoming') {
                      const payment = row.payment;
                      return (
                        <TableRow
                          key={`incoming-${payment.id}`}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() =>
                            navigate(
                              `/incoming-payments?status=APPROVED&payment=${encodeURIComponent(payment.id)}`,
                            )
                          }
                        >
                          <TableCell>{formatTime(payment.createdAt)}</TableCell>
                          <TableCell>
                            <Chip size="small" color="success" variant="outlined" label="CREDIT" />
                          </TableCell>
                          {/* We do not record which rail an arriving payment came
                          in on — the partner does not tell us — so the honest
                          answer is a dash rather than repeating the direction. */}
                          <TableCell>—</TableCell>
                          <TableCell align="right">
                            {formatAmount(payment.amountCents, payment.currency)}
                          </TableCell>
                          <TableCell>{payment.payerName ?? '—'}</TableCell>
                          <TableCell>{payment.paymentDetails || '—'}</TableCell>
                          <TableCell>
                            <StatusBadge kind="payment" value="SETTLED" />
                          </TableCell>
                        </TableRow>
                      );
                    }

                    const payment = row.payment;
                    return (
                      <TableRow
                        key={`outgoing-${payment.id}`}
                        hover
                        sx={{ cursor: 'pointer' }}
                        selected={payment.id === selectedId}
                        onClick={() => setSelectedId(payment.id)}
                      >
                        <TableCell>{formatTime(payment.createdAt)}</TableCell>
                        <TableCell>
                          <Chip size="small" color="warning" variant="outlined" label="DEBIT" />
                        </TableCell>
                        <TableCell>{payment.paymentType}</TableCell>
                        <TableCell align="right">
                          {formatAmount(payment.amountCents, payment.currency)}
                        </TableCell>
                        <TableCell>{payment.creditorName}</TableCell>
                        <TableCell>{payment.reference || '—'}</TableCell>
                        <TableCell>
                          <StatusBadge kind="payment" value={payment.status} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        No payments.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}
      <Drawer
        anchor="right"
        open={selectedId !== null}
        onClose={() => {
          setSelectedId(null);
          onReviewClose?.();
        }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, maxWidth: '100vw' } }}
      >
        <Box sx={{ p: 3 }}>
          <Button
            sx={{ mb: 2 }}
            onClick={() => {
              setSelectedId(null);
              onReviewClose?.();
            }}
          >
            Close details
          </Button>
          {detail.isError ? (
            <Alert severity="error">Payment details could not be loaded.</Alert>
          ) : detail.isLoading || selected === null ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Transaction</Typography>
                <StatusBadge kind="payment" value={selected.status} />
              </Stack>

              <Stack spacing={1.5}>
                <Field
                  label="Amount"
                  value={formatAmount(selected.amountCents, selected.currency)}
                />
                <Field label="Type" value={selected.paymentType} />
                <Field
                  label="Creditor"
                  value={`${selected.creditorName} · ${selected.creditorIban}`}
                />
                <Field label="Debtor IBAN" value={selected.debtorIban} />
                <Field label="Reference" value={selected.reference || '—'} />
                <Field label="Created" value={formatTime(selected.createdAt)} />
                {selected.scheduledFor !== null && (
                  <Field label="Scheduled for" value={formatTime(selected.scheduledFor)} />
                )}
                {selected.failureReason !== null && (
                  <Field label="Failure reason" value={selected.failureReason} />
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />
              <ReviewDocumentsPanel key={selected.id} kind="payment" id={selected.id} canWrite={canAct} />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                History
              </Typography>
              {selected.history.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No status changes yet.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {selected.history.map((h, i) => (
                    <Box key={`${h.toStatus}-${String(i)}`} sx={{ fontSize: 13 }}>
                      <Typography variant="body2">
                        {(h.fromStatus ?? '—') + ' → ' + h.toStatus}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block' }}
                      >
                        {formatTime(h.createdAt)}
                        {h.reason !== null ? ` · ${h.reason}` : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}

              {canAct && isOpenState && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        setReason('');
                        setDialog({ action: 'execute' });
                      }}
                    >
                      Execute
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setReason('');
                        setDialog({ action: 'reject' });
                      }}
                    >
                      Reject
                    </Button>
                  </Stack>
                </>
              )}
            </>
          )}
        </Box>
      </Drawer>

      <Dialog open={dialog !== null} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialog?.action === 'execute' ? 'Execute payment' : 'Reject payment'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {dialog?.action === 'execute'
              ? 'Manually settle this send request. Funds move on the ledger.'
              : 'Reject this send request — it will be marked FAILED.'}
          </Typography>
          {dialog?.action === 'execute' && selectedId && (
            <PaymentScreeningPanel
              key={selectedId}
              path={`/admin/payments/${selectedId}`}
              onReady={setScreeningReady}
            />
          )}
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mutation.error instanceof Error ? mutation.error.message : 'Action failed'}
            </Alert>
          )}
          <TextField
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you taking this action?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={dialog?.action === 'reject' ? 'error' : 'success'}
            disabled={
              reason.trim().length < 4 ||
              mutation.isPending ||
              selectedId === null ||
              dialog === null ||
              (dialog.action === 'execute' && !screeningReady)
            }
            onClick={() => {
              if (selectedId !== null && dialog !== null) {
                mutation.mutate({ id: selectedId, action: dialog.action, reason: reason.trim() });
              }
            }}
          >
            {mutation.isPending ? 'Working…' : dialog?.action === 'execute' ? 'Execute' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
